import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import usePageStylesheet from '../util/use-page-stylesheet';
import EyesNav from '../components/EyesNav';
import EyesFooter from '../components/EyesFooter';
import EyeViewer from '../components/EyeViewer';
import prophecyVideo from '../assets/videos/the-eyes-prophecy.mp4';
import prophecyPoster from '../assets/imgs/prophecy-poster.jpg';
import casaMalkaImg from '../assets/imgs/casamalka.webp';
import casaSymbolImg from '../assets/imgs/casa-symbol.webp';

/**
 * The landing page, ported 1:1 from eyes/index.html.
 *
 * Markup, styles and behaviour are the originals: the stylesheet lives at
 * public/eyes/eyes.css and the scripts that shipped inline in the document
 * are the effects below, with teardown added so they do not outlive the
 * route. The tree is rendered through a portal because the original CSS
 * targets `body > nav` and expects the sections to be children of <body>.
 */
export default function Splash() {
  usePageStylesheet('/eyes/eyes.css');

  useEffect(() => {
    const cleanups = [];

    // ===== eyes-cinematic-motion-js =====
    (() => {
      const reduce = window.matchMedia(
        '(prefers-reduced-motion: reduce)',
      ).matches;
      const hero = document.querySelector('.hero');
      const ord = document.querySelector('#why-ordinals');

      // This script also added the header's glass-on-scroll; that part now
      // lives in EyesNav, which every page using the header shares.

      if (reduce) return;

      // Elegant staggered section reveals.
      const selectors = [
        '.ey',
        '.title',
        '.copy',
        '.bounty-card',
        '.news-clipping',
        '.clipping-card',
        '.ordinal-point',
        '.faq-item',
      ];
      const els = [...document.querySelectorAll(selectors.join(','))];
      els.forEach((el, i) => {
        el.classList.add('reveal-motion');
        if (i % 4 === 1) el.classList.add('reveal-delay-1');
        if (i % 4 === 2) el.classList.add('reveal-delay-2');
        if (i % 4 === 3) el.classList.add('reveal-delay-3');
      });
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => {
            if (e.isIntersecting) {
              e.target.classList.add('is-visible');
              io.unobserve(e.target);
            }
          });
        },
        { threshold: 0.1, rootMargin: '0px 0px -35px 0px' },
      );
      els.forEach((el) => io.observe(el));
      cleanups.push(() => io.disconnect());

      // Draw Why Ordinals line and illuminate symbols once.
      if (ord) {
        const oi = new IntersectionObserver(
          (es) => {
            es.forEach((e) => {
              if (e.isIntersecting) {
                ord.classList.add('ordinals-live');
                oi.disconnect();
              }
            });
          },
          { threshold: 0.28 },
        );
        oi.observe(ord);
        cleanups.push(() => oi.disconnect());
      }

      // Hero parallax + restrained cursor glow.
      if (hero) {
        hero.classList.add('hero-parallax-active');
        const glow = document.createElement('span');
        glow.className = 'hero-cursor-glow';
        hero.appendChild(glow);

        const onMove = (e) => {
          const r = hero.getBoundingClientRect();
          glow.style.left = e.clientX - r.left + 'px';
          glow.style.top = e.clientY - r.top + 'px';
        };
        const onLeave = () => {
          glow.style.opacity = '.12';
        };
        const onEnter = () => {
          glow.style.opacity = '.42';
        };
        const parallax = () => {
          const r = hero.getBoundingClientRect();
          const shift = Math.max(-28, Math.min(28, -r.top * 0.055));
          hero.style.setProperty('--hero-shift', shift + 'px');
        };

        hero.addEventListener('mousemove', onMove);
        hero.addEventListener('mouseleave', onLeave);
        hero.addEventListener('mouseenter', onEnter);
        parallax();
        window.addEventListener('scroll', parallax, { passive: true });

        cleanups.push(() => {
          hero.removeEventListener('mousemove', onMove);
          hero.removeEventListener('mouseleave', onLeave);
          hero.removeEventListener('mouseenter', onEnter);
          window.removeEventListener('scroll', parallax);
          glow.remove();
        });
      }
    })();

    // ===== exact-original-glitch-js / golden-age-original-glitch-canvas =====
    // The same block glitch, drawn over the "26" and the Golden Age line.
    [
      ['.mint-glitch-original-canvas', '.mint-glitch-original'],
      ['.golden-glitch-canvas', '.golden-glitch-text'],
    ].forEach(([canvasSelector, textSelector]) => {
      const canvas = document.querySelector(canvasSelector);
      const text = document.querySelector(textSelector);
      if (!canvas || !text) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const sizeCanvas = () => {
        canvas.width = text.offsetWidth;
        canvas.height = text.offsetHeight;
      };

      sizeCanvas();
      window.addEventListener('resize', sizeCanvas);

      const timer = setInterval(() => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        for (let x = 0; x < canvas.width; x += 6) {
          if (Math.random() > 0.85) {
            const y = 6 * Math.floor(Math.random() * (canvas.height / 6));
            const colors = ['#00ffff', '#ff0044', '#e53935', '#ffffff'];
            ctx.fillStyle = colors[Math.floor(Math.random() * colors.length)];
            ctx.globalAlpha = 0.6 * Math.random() + 0.2;
            ctx.fillRect(x, y, 6 * (Math.floor(4 * Math.random()) + 1), 6);
          }
        }
      }, 80);

      cleanups.push(() => {
        clearInterval(timer);
        window.removeEventListener('resize', sizeCanvas);
      });
    });

    // ===== eyes-mint-countdown =====
    (() => {
      // Nov 26, 2026 at 00:00 PST; fixed timezone offset avoids visitor timezone drift.
      const target = Date.parse('2026-11-26T00:00:00-08:00');
      const fields = ['mg-days', 'mg-hours', 'mg-minutes', 'mg-seconds'].map(
        (id) => document.getElementById(id),
      );
      if (fields.some((e) => !e)) return;

      let interval;
      const update = () => {
        const left = Math.max(0, Math.ceil((target - Date.now()) / 1000));
        const days = Math.floor(left / 86400);
        const hours = Math.floor((left % 86400) / 3600);
        const minutes = Math.floor((left % 3600) / 60);
        const seconds = left % 60;
        [days, hours, minutes, seconds].forEach((v, i) => {
          fields[i].textContent = String(v).padStart(2, '0');
        });
        if (left === 0) clearInterval(interval);
      };
      interval = setInterval(update, 1000);
      update();

      cleanups.push(() => clearInterval(interval));
    })();


    // The in-page anchors are reached from /watch as `/#section`, which lands
    // here before this tree exists, so the browser cannot make the jump.
    if (window.location.hash) {
      const target = document.querySelector(window.location.hash);
      if (target) target.scrollIntoView();
    }

    return () => cleanups.forEach((fn) => fn());
  }, []);

  const page = createPortal(
    <>
      <div className="site-smoke" aria-hidden="true">
        {' '}
        <span className="smoke s1">
        </span>
        <span className="smoke s2">
        </span>
        <span className="smoke s3">
        </span>
        <span className="smoke s4">
        </span>
        {' '}
      </div>
      {' '}
      <EyesNav home />
      {' '}
      <header className="hero" id="about">
        <div className="hero-eye-glows" aria-hidden="true">
          <span className="hero-eye eye-left">
          </span>
          <span className="hero-eye eye-right">
          </span>
        </div>
        <div className="wrap heroGrid">
          <div>
            <div className="ey">
              21,000 BITCOIN ORDINALS · THE EYES
            </div>
            <h1>
              THE EYES
              <br />
              {"ARE "}
              <span className="hero-red-word">
                ALWAYS
              </span>
              <br />
              <span className="hero-red-word">
                WATCHING
              </span>
            </h1>
            <p>
              21,000 Ordinals by Mr. Black. Hidden within the art is a prophecy of the Bitcoin Golden Age. Fear God, not man.
            </p>
            <div className="acts">
              <a className="btn r" href="#mint">
                NOV 26
              </a>
              <a className="btn" href="#gallery">
                VIEW COLLECTION
              </a>
            </div>
            <div className="facts">
              <div className="fact">
                <i>
                  ₿
                </i>
                <span>
                  <b>
                    ON BITCOIN
                  </b>
                  Inscribed forever on the world's strongest network.
                </span>
              </div>
              <div className="fact">
                <i>
                  ◉
                </i>
                <span>
                  <b>
                    21,000 UNIQUE
                  </b>
                  Handcrafted art. No two the same.
                </span>
              </div>
              <div className="fact">
                <i>
                  ✣
                </i>
                <span>
                  <b>
                    A HIGHER MESSAGE
                  </b>
                  Art, story and a community built to last.
                </span>
              </div>
            </div>
          </div>
        </div>
      </header>
      {' '}
      <section className="wrap mint mint-relaunch-original mint-game-v3" id="mint" aria-label="Mint opens November 26">
        <div className="mg-ambient" aria-hidden="true">
          <div className="mg-core">
          </div>
          <div className="mg-light-beam mg-light-beam-a">
          </div>
          <div className="mg-light-beam mg-light-beam-b">
          </div>
          <div className="mg-smoke">
          </div>
          <div className="mg-embers">
          </div>
        </div>
        <div className="mg-content">
          <div className="mg-eyebrow">
            <span className="mg-live-dot">
            </span>
            <span>
              THE EYES
            </span>
            <span className="mg-divider">
            </span>
            <span>
              A NEW CHAPTER
            </span>
          </div>
          <h2 className="mg-date">
            <span>
              NOVEMBER
            </span>
            <span className="mint-glitch-stage">
              <strong className="mint-glitch-original" data-text="26">
                26
              </strong>
              <canvas className="mint-glitch-original-canvas" aria-hidden="true">
              </canvas>
            </span>
          </h2>
          <div className="mg-separator" aria-hidden="true">
            <span>
            </span>
            <i>
              ✧
            </i>
            <span>
            </span>
          </div>
          <div className="mg-countdown" role="timer" aria-label="Time until minting opens on November 26, 2026 at midnight Pacific Time">
            <div className="mg-clock" aria-live="off">
              <div className="mg-clock-unit">
                <span id="mg-days">
                  00
                </span>
                <small>
                  DAYS
                </small>
              </div>
              <b className="mg-clock-sep">
                :
              </b>
              <div className="mg-clock-unit">
                <span id="mg-hours">
                  00
                </span>
                <small>
                  HOURS
                </small>
              </div>
              <b className="mg-clock-sep">
                :
              </b>
              <div className="mg-clock-unit">
                <span id="mg-minutes">
                  00
                </span>
                <small>
                  MINUTES
                </small>
              </div>
              <b className="mg-clock-sep">
                :
              </b>
              <div className="mg-clock-unit">
                <span id="mg-seconds">
                  00
                </span>
                <small>
                  SECONDS
                </small>
              </div>
            </div>
          </div>
          <div className="mg-golden">
            <span className="mint-glitch-stage golden-glitch-stage">
              <strong className="mint-glitch-original golden-glitch-text" data-text="THE BITCOIN GOLDEN AGE WILL BEGIN">
                THE BITCOIN GOLDEN AGE WILL BEGIN
              </strong>
              <canvas className="mint-glitch-original-canvas golden-glitch-canvas" aria-hidden="true">
              </canvas>
            </span>
          </div>
          <div className="mg-footer">
            <span>
              ◈ &nbsp; NOV 26, 2026
            </span>
            <span className="mg-footer-middle">
              A NEW ERA IS COMING
            </span>
            <span>
              ● &nbsp; A NEW CHAPTER AWAITS
            </span>
          </div>
        </div>
      </section>
      {' '}
      <section id="lore" className="prophecy-home-section">
        <div className="wrap prophecy-home-layout">
          <div className="prophecy-home-intro">
            <div className="ey">
              THE PROPHECY
            </div>
            <h2 className="title">
              {"A NEW AGE "}
              <span className="humanity-red">
                FOR HUMANITY.
              </span>
            </h2>
            <p className="copy">
              On Thanksgiving night in 2023, a voice spoke to me: “The Eyes Are Always Watching.” I believe hidden within this art are the wonders of the universe and a guide to living in a new age for humanity. A new world is coming.
            </p>
            <a className="btn" href="#mint">
              NOV 26
            </a>
          </div>
          <div className="prophecy-home-players">
            <div className="prophecy-home-player">
              <div className="prophecy-video prophecy-video-new">
                <video controls playsInline preload="metadata" poster={prophecyPoster} aria-label="Mr. Black & The Prophecy">
                  <source src={prophecyVideo} type="video/mp4" />
                  {"Your browser cannot play this video. "}
                  <a href={prophecyVideo}>
                    Open the video file
                  </a>
                  .
                </video>
              </div>
              <div className="cap">
                <div>
                  <b>
                    THE BITCOIN PROPHECY
                  </b>
                </div>
              </div>
            </div>
            <div className="prophecy-home-player">
              <div className="prophecy-video prophecy-locked-video" aria-label="Full Prophecy video locked" role="img">
                <div className="prophecy-locked-ambient" aria-hidden="true">
                </div>
                <div className="prophecy-locked-content">
                  <svg width="27" height="32" viewBox="0 0 27 32" fill="none" aria-hidden="true">
                    <path d="M6.5 13V8.5a7 7 0 0 1 14 0V13" stroke="currentColor" strokeWidth="1.7" />
                    <rect x="3" y="13" width="21" height="16" rx="2" stroke="currentColor" strokeWidth="1.7" />
                    <path d="M13.5 19.5v4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                  </svg>
                  <span className="prophecy-locked-kicker">
                    THE EYES · A NEW CHAPTER
                  </span>
                  <strong>
                    THE FULL PROPHECY
                  </strong>
                  <span className="prophecy-locked-line">
                  </span>
                  <p>
                    THE FULL PROPHECY WILL REVEAL IN TIME
                  </p>
                </div>
              </div>
              <div className="cap">
                <div>
                  <b>
                    {"THE FULL PROPHECY "}
                    <span className="prophecy-inline-status">
                      LOCKED · AWAITING REVEAL
                    </span>
                  </b>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {' '}
      <section id="bounty" className="bounty-section">
        {' '}
        <div className="wrap bounty-wrap">
          {' '}
          <div className="bounty-intro">
            {' '}
            <div className="ey">
              THE LEGENDS BOUNTY HUNT
            </div>
            {' '}
            <h2 className="title bounty-title">
              <span className="red">
                $100,000 IN BTC.
              </span>
              <br />
              HIDDEN IN THE EYES.
            </h2>
            {' '}
            <p className="copy bounty-copy">
              {"Hidden among the 21,000 Eyes are 120 one-of-one Legends inspired by icons of culture, history and myth. "}
              <b>
                44 of them carry Bitcoin bounties.
              </b>
            </p>
            {' '}
          </div>
          {' '}
          <div className="most-wanted-head">
            {' '}
            <div>
              {' '}
              <div className="ey">
                THE MOST WANTED
              </div>
              {' '}
              <h3>
                {"4 LEGENDS. "}
                <span className="red">
                  $5,000 EACH.
                </span>
              </h3>
              {' '}
            </div>
            {' '}
            <div className="bounty-total">
              ₿ BTC BOUNTIES
            </div>
            {' '}
          </div>
          {' '}
          <div className="wanted-grid">
            {' '}
            <div className="wanted-card">
              <span>
                01
              </span>
              <div className="wanted-image">
                <img src="/eyes/assets/08fc54a921.png" alt="MR. BLACK" />
              </div>
              <b>
                MR. BLACK
              </b>
              <small>
                $5,000 BTC BOUNTY
              </small>
            </div>
            {' '}
            <div className="wanted-card">
              <span>
                02
              </span>
              <div className="wanted-image">
                <img src="/eyes/assets/46de903b15.png" alt="JOKES ON YOU" />
              </div>
              <b>
                JOKES ON YOU
              </b>
              <small>
                $5,000 BTC BOUNTY
              </small>
            </div>
            {' '}
            <div className="wanted-card">
              <span>
                03
              </span>
              <div className="wanted-image">
                <img src="/eyes/assets/4624407d4f.png" alt="SON OF GOD" />
              </div>
              <b>
                SON OF GOD
              </b>
              <small>
                $5,000 BTC BOUNTY
              </small>
            </div>
            {' '}
            <div className="wanted-card">
              <span>
                04
              </span>
              <div className="wanted-image">
                <img src="/eyes/assets/0123456789.png" alt="KING POET" />
              </div>
              <b>
                ILLUMINATI, ALL
THROUGH MY BODY
              </b>
              <small>
                $5,000 BTC BOUNTY
              </small>
            </div>
            {' '}
          </div>
          {' '}
          <div className="bounty-bottom">
            {' '}
            <div className="bounty-more">
              {' '}
              <div className="ey">
                AND 40 MORE
              </div>
              {' '}
              <h3>
                {"40 HIDDEN LEGENDS. "}
                <span className="red">
                  $2,000 EACH.
                </span>
              </h3>
              {' '}
              <p>
                44 winning Legends are hidden within The Eyes collection, each carrying a Bitcoin bounty. The hunt is on.
              </p>
              {' '}
            </div>
            {' '}
            <div className="hunt-callout">
              {' '}
              <b>
                44 WINNING EYES.
              </b>
              {' '}
              <b>
                $100,000 IN BITCOIN.
              </b>
              {' '}
              <span>
                THE HUNT IS ON.
              </span>
              {' '}
            </div>
            {' '}
          </div>
          {' '}
          <div className="casa-bounty-feature" aria-label="Casa Malka partnership reward">
            <div className="casa-bounty-copy">
              <div className="casa-bounty-title-group">
                <div className="casa-bounty-kicker">
                  {"THE CASA MALKA PARTNERSHIP "}
                  <span className="casa-bounty-kicker-line">
                  </span>
                  {" EXCLUSIVE ORDINAL REWARD"}
                </div>
                <h3>
                  {"FIND THE ORDINAL. "}
                  <span>
                    CLAIM THE YEAR.
                  </span>
                </h3>
              </div>
              <div className="casa-bounty-description">
                <p>
                  {"Hidden within The Eyes collection are 3 exclusive "}
                  <strong>
                    Casa Malka Ordinals.
                  </strong>
                  {" Discover one and unlock "}
                  <strong>
                    a year's supply of premium Casa Malka tequila.
                  </strong>
                </p>
              </div>
            </div>
          </div>
          {' '}
          <div className="bounty-actions">
            {' '}
            <a className="btn r" href="#mint">
              NOV 26
            </a>
            {' '}
            <a className="btn" href="#">
              JOIN THE DISCORD FOR MORE INFO
            </a>
            {' '}
          </div>
          {' '}
        </div>
        {' '}
      </section>
      {' '}
      <section id="casa-malka" className="casa-section" aria-labelledby="casa-malka-heading">
        <div className="wrap casa-layout">
          <div className="casa-photo">
            <img src={casaMalkaImg} alt="Casa Malka tequila bottle surrounded by elegant gold and black gloves" loading="lazy" decoding="async" />
          </div>
          <div className="casa-content">
            <div className="casa-brandmark">
              <img src={casaSymbolImg} alt="Casa Malka emblem" loading="lazy" />
            </div>
            <div className="casa-kicker">
              THE EYES × CASA MALKA
            </div>
            <h2 id="casa-malka-heading">
              {"CASA "}
              <span>
                MALKA.
              </span>
            </h2>
            <p className="casa-tagline">
              A new era of luxury, culture and craftsmanship.
            </p>
            <p className="casa-body">
              Co-founded by The Eyes creator Jayson Winer, Casa Malka is a premium, additive-free tequila brand pioneering a new approach to the spirits industry.
            </p>
            <p className="casa-body">
              As one of the first tequila brands to launch through Web3, Casa Malka is bridging the gap between digital ownership and real-world luxury, using The Eyes collection to introduce a new generation to the brand.
            </p>
            <div className="casa-closing">
              Where Bitcoin, culture and craftsmanship come together.
            </div>
            <a className="casa-cta" href="https://casamalka.com/" target="_blank" rel="noopener noreferrer">
              {"VIEW CASA MALKA "}
              <span aria-hidden="true">
                ↗
              </span>
            </a>
          </div>
        </div>
      </section>
      {' '}
      <section id="gallery" className="viewer-section">
        {' '}
        <div className="wrap viewer-wrap">
          {' '}
          <div className="viewer-intro">
            {' '}
            <div className="ey">
              THE ARTWORK VIEWER
            </div>
            {' '}
            <h2>
              {"EXPLORE THE "}
              <span className="red">
                EYES.
              </span>
            </h2>
            {' '}
            <p>
              Explore The Eyes, discover Legends and search the collection by Eye number, name, inscription or wallet address.
            </p>
            {' '}
          </div>
          {' '}
          <EyeViewer />
          {' '}
          <div className="viewer-loading" id="viewerLoading">
            {' '}
            <span>
            </span>
            <span>
            </span>
            <span>
            </span>
            {' '}
            <b>
              LOADING MORE EYES
            </b>
            {' '}
          </div>
          {' '}
        </div>
        {' '}
      </section>
      {' '}
      <section id="why-ordinals" className="ordinals-horizontal">
        {' '}
        <div className="wrap ordinals-horizontal-wrap">
          {' '}
          <div className="ordinals-horizontal-head">
            {' '}
            <div className="ey">
              BUILT ON BITCOIN
            </div>
            {' '}
            <h2>
              {"WHY "}
              <span className="red">
                ORDINALS?
              </span>
            </h2>
            {' '}
            <p>
              Digital art that doesn't depend on us to survive.
            </p>
            {' '}
          </div>
          {' '}
          <div className="ordinal-flow">
            {' '}
            <div className="ordinal-flow-line">
            </div>
            {' '}
            <article className="ordinal-point">
              {' '}
              <div className="ordinal-symbol">
                {' '}
                <svg viewBox="0 0 64 64" aria-hidden="true">
                  {' '}
                  <circle cx="32" cy="32" r="21">
                  </circle>
                  {' '}
                  <path d="M25 19v26M36 17v29M22 24h18c5 0 8 3 8 7s-3 7-8 7H22M24 31h17">
                  </path>
                  {' '}
                </svg>
                {' '}
              </div>
              {' '}
              <span>
                INSCRIBED
              </span>
              {' '}
              <h3>
                ON BITCOIN
              </h3>
              {' '}
              <p>
                Each Eye is inscribed directly onto Bitcoin.
              </p>
              {' '}
            </article>
            {' '}
            <article className="ordinal-point">
              {' '}
              <div className="ordinal-symbol">
                {' '}
                <svg viewBox="0 0 64 64" aria-hidden="true">
                  {' '}
                  <path d="M18 32c0-8 5-14 13-14 9 0 12 9 17 14-5 5-8 14-17 14-8 0-13-6-13-14Z">
                  </path>
                  {' '}
                  <path d="M46 32c0-8-5-14-13-14-9 0-12 9-17 14 5 5 8 14 17 14 8 0 13-6 13-14Z">
                  </path>
                  {' '}
                </svg>
                {' '}
              </div>
              {' '}
              <span>
                PERMANENT
              </span>
              {' '}
              <h3>
                NO EXPIRY
              </h3>
              {' '}
              <p>
                No hosting bill. No platform required. No delete button.
              </p>
              {' '}
            </article>
            {' '}
            <article className="ordinal-point">
              {' '}
              <div className="ordinal-symbol">
                {' '}
                <svg viewBox="0 0 64 64" aria-hidden="true">
                  {' '}
                  <rect x="13" y="20" width="38" height="29" rx="6">
                  </rect>
                  {' '}
                  <path d="M13 27h38M39 34h12v9H39a4.5 4.5 0 0 1 0-9Z">
                  </path>
                  {' '}
                  <circle cx="42" cy="38.5" r="1.5">
                  </circle>
                  {' '}
                </svg>
                {' '}
              </div>
              {' '}
              <span>
                OWNED
              </span>
              {' '}
              <h3>
                IN YOUR WALLET
              </h3>
              {' '}
              <p>
                Your Eye is held and verified through Bitcoin itself.
              </p>
              {' '}
            </article>
            {' '}
            <article className="ordinal-point">
              {' '}
              <div className="ordinal-symbol eye-symbol">
                {' '}
                <svg viewBox="0 0 64 64" aria-hidden="true">
                  {' '}
                  <path d="M7 32s9-14 25-14 25 14 25 14-9 14-25 14S7 32 7 32Z">
                  </path>
                  {' '}
                  <circle cx="32" cy="32" r="8">
                  </circle>
                  {' '}
                  <circle cx="32" cy="32" r="2">
                  </circle>
                  {' '}
                </svg>
                {' '}
              </div>
              {' '}
              <span>
                FOREVER
              </span>
              {' '}
              <h3>
                BUILT TO OUTLIVE US
              </h3>
              {' '}
              <p>
                Art preserved as a permanent record of this moment.
              </p>
              {' '}
            </article>
            {' '}
          </div>
          {' '}
          <div className="ordinal-horizontal-end">
            {' '}
            <strong>
              {"21,000 EYES. "}
              <em>
                FOREVER ON BITCOIN.
              </em>
            </strong>
            {' '}
          </div>
          {' '}
        </div>
        {' '}
      </section>
      {' '}
      <section id="news" className="clippings-news">
        {' '}
        <div className="wrap clippings-wrap">
          {' '}
          <div className="clippings-head">
            {' '}
            <div className="ey">
              PRESS & MEDIA
            </div>
            {' '}
            <h2>
              {"FROM THE "}
              <span className="red">
                HEADLINES.
              </span>
            </h2>
            {' '}
            <p>
              Selected stories, features and coverage surrounding Mr. Black and The Eyes.
            </p>
            {' '}
          </div>
          {' '}
          <div className="clippings-board">
            <a className="clip clip-1" style={{'--rot': '-1.1deg'}} href="https://decrypt.co/240924/mr-black-to-unveil-groundbreaking-21000-piece-ordinal-collection-on-bitcoin" target="_blank" rel="noopener">
              {' '}
              <div className="clip-mast">
                DECRYPT
              </div>
              {' '}
              <div className="clip-rule">
              </div>
              {' '}
              <div className="clip-meta">
                <span>
                  JUL 2024
                </span>
                <span>
                  PRESS CLIPPING
                </span>
              </div>
              {' '}
              <h3>
                Mr. Black to Unveil Groundbreaking 21,000-Piece Ordinal Collection on Bitcoin
              </h3>
              {' '}
              <p>
                Read the full story online.
              </p>
              {' '}
              <div className="clip-read">
                {"CONTINUE READING "}
                <b>
                  ↗
                </b>
              </div>
              {' '}
            </a>
            <a className="clip clip-2" style={{'--rot': '.7deg'}} href="https://resident.com/business-and-finance/2024/07/25/groundbreaking-work-from-renowned-artist-mr-black-to-be-unveiled-on-x-with-21000-piece-ordinal-collection-on-bitcoin" target="_blank" rel="noopener">
              {' '}
              <div className="clip-mast">
                RESIDENT
              </div>
              {' '}
              <div className="clip-rule">
              </div>
              {' '}
              <div className="clip-meta">
                <span>
                  JUL 2024
                </span>
                <span>
                  PRESS CLIPPING
                </span>
              </div>
              {' '}
              <h3>
                Groundbreaking Work From Renowned Artist Mr. Black
              </h3>
              {' '}
              <p>
                Read the full story online.
              </p>
              {' '}
              <div className="clip-read">
                {"CONTINUE READING "}
                <b>
                  ↗
                </b>
              </div>
              {' '}
            </a>
            <a className="clip clip-3" style={{'--rot': '-.45deg'}} href="https://t2conline.com/mr-black-is-back-and-this-time-in-times-square/" target="_blank" rel="noopener">
              {' '}
              <div className="clip-mast">
                TIMES SQUARE CHRONICLES
              </div>
              {' '}
              <div className="clip-rule">
              </div>
              {' '}
              <div className="clip-meta">
                <span>
                  PRESS
                </span>
                <span>
                  PRESS CLIPPING
                </span>
              </div>
              {' '}
              <h3>
                Mr. Black Is Back and This Time in Times Square
              </h3>
              {' '}
              <p>
                Read the full story online.
              </p>
              {' '}
              <div className="clip-read">
                {"CONTINUE READING "}
                <b>
                  ↗
                </b>
              </div>
              {' '}
            </a>
            <a className="clip clip-4" style={{'--rot': '1deg'}} href="https://www.binance.com/en/square/post/26354530425794" target="_blank" rel="noopener">
              {' '}
              <div className="clip-mast">
                BINANCE SQUARE
              </div>
              {' '}
              <div className="clip-rule">
              </div>
              {' '}
              <div className="clip-meta">
                <span>
                  BITCOIN
                </span>
                <span>
                  PRESS CLIPPING
                </span>
              </div>
              {' '}
              <h3>
                The Eyes Are Always Watching
              </h3>
              {' '}
              <p>
                Read the full story online.
              </p>
              {' '}
              <div className="clip-read">
                {"CONTINUE READING "}
                <b>
                  ↗
                </b>
              </div>
              {' '}
            </a>
            <a className="clip clip-8" style={{'--rot': '.8deg'}} href="https://www.youtube.com/watch?v=_vAdfCKnRO4&t=29s" target="_blank" rel="noopener">
              {' '}
              <div className="clip-mast">
                WATCH
              </div>
              {' '}
              <div className="clip-rule">
              </div>
              {' '}
              <div className="clip-meta">
                <span>
                  VIDEO
                </span>
                <span>
                  PRESS CLIPPING
                </span>
              </div>
              {' '}
              <h3>
                Mr. Black on YouTube
              </h3>
              {' '}
              <p>
                Watch the video on YouTube.
              </p>
              {' '}
              <div className="clip-read">
                {"WATCH VIDEO "}
                <b>
                  ↗
                </b>
              </div>
              {' '}
            </a>
          </div>
          {' '}
        </div>
        {' '}
      </section>
      {' '}
      <section id="faq" className="faq-section">
        <div className="wrap faq-wrap">
          {' '}
          <div className="faq-head">
            <div className="ey">
              FAQ
            </div>
            <h2 className="title">
              {"EVERYTHING YOU "}
              <span className="red">
                NEED TO KNOW.
              </span>
            </h2>
            <p className="copy">
              The artwork, the prophecy, Bitcoin Ordinals and everything in between.
            </p>
          </div>
          {' '}
          <div className="faq-list">
            <details className="faq-item">
              <summary>
                <span>
                  What is The Eyes Are Always Watching?
                </span>
                <b>
                  +
                </b>
              </summary>
              <div className="faq-answer">
                <p>
                  21,000 unique artworks inscribed permanently on Bitcoin using Ordinals, created by Mr. Black.
                </p>
              </div>
            </details>
            <details className="faq-item">
              <summary>
                <span>
                  What wallet can I use?
                </span>
                <b>
                  +
                </b>
              </summary>
              <div className="faq-answer">
                <p>
                  UniSat, Xverse or OKX Wallet.
                </p>
              </div>
            </details>
            <details className="faq-item">
              <summary>
                <span>
                  What address type do I need?
                </span>
                <b>
                  +
                </b>
              </summary>
              <div className="faq-answer">
                <p>
                  Use a Taproot address, which starts with bc1p.
                </p>
              </div>
            </details>
            <details className="faq-item">
              <summary>
                <span>
                  Where can I see my Eye?
                </span>
                <b>
                  +
                </b>
              </summary>
              <div className="faq-answer">
                <p>
                  Inscribed Eyes can be viewed through Bitcoin Ordinals explorers, including Ordinals.com.
                </p>
              </div>
            </details>
            <details className="faq-item">
              <summary>
                <span>
                  Where can I trade?
                </span>
                <b>
                  +
                </b>
              </summary>
              <div className="faq-answer">
                <p>
                  Satflow.
                </p>
              </div>
            </details>
            <details className="faq-item">
              <summary>
                <span>
                  Still have questions?
                </span>
                <b>
                  +
                </b>
              </summary>
              <div className="faq-answer">
                <p>
                  Ask in Discord and a member of the team will be happy to help!
                </p>
              </div>
            </details>
          </div>
        </div>
      </section>
      {' '}
      <EyesFooter home />
    </>,
    document.body,
  );

  return page;
}
